import React, { ChangeEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import OfferAirportSelectBox from '../Airport/OfferAirportSelectBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import CharterFlightOfferAirportOptionsList from './CharterFlightOfferAirportOptionsList'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { airportLocationToAirport } from 'lib/flights/flightUtils'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import FloatingTooltip from 'LuxKit/FloatingTooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

const DropdownContainer = styled.div`
  position: relative;
`

interface Props {
  open: boolean
  selectedAirport?: App.Airport
  className?: string
}

function CharterFlightOfferAirportSelect({ open, selectedAirport, className }: Props) {
  const trigger = useRef<HTMLButtonElement>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const isMobile = useScreenSizeOnly('mobile')
  const [searchPhrase, setSearchPhrase] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('AIRPORT_FILTER_CLOSED')
  }, [dispatch, trackEvent])

  const onAirportSelect = useCallback((airport: App.AirportLocation) => {
    dispatch({
      type: OfferPageActions.setAirport,
      airport: airportLocationToAirport(airport),
    })
    dispatch({
      type: OfferPageActions.setFlights,
      flights: true,
    })
    trackEvent('AIRPORT_FILTER_SELECTED')
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, trackEvent])

  useEffect(() => {
    if (open && !isMobile) {
      inputRef.current?.focus()
    }
  }, [isMobile, open])

  const handleSearchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.target.value)
  }, [])

  return (
    <DropdownContainer className={className}>
      <OfferAirportSelectBox
        ref={trigger}
        disabled={isAgentHubEnabled}
        style={isAgentHubEnabled ? { cursor: 'not-allowed' } : undefined}
      />
      {!isAgentHubEnabled && (
        <DropdownList
          size="M"
          open={open && !isMobile}
          anchorRef={trigger}
          triggerRef={trigger}
          title="Where will you depart from?"
          subtitle="Select your preferred airport"
          onClose={closeFilter}
          headerExtension={<TextInputOnly
            placeholder="Search by city or airport"
            noValidationMessage
            noValidationSpacing
            value={searchPhrase}
            onChange={handleSearchPhraseInputChange}
            endIcon={<LineSearchIcon />}
            ref={inputRef}
          />}
        >
          <CharterFlightOfferAirportOptionsList
            selectedAirport={selectedAirport}
            onSelectAirport={onAirportSelect}
            searchPhrase={searchPhrase}
          />
        </DropdownList>
      )}
      {isAgentHubEnabled && selectedAirport && (
        <FloatingTooltip
          triggerRef={trigger}
          description={
            <Group direction="vertical" gap={8}>
              <BodyText variant="small" weight="normal" as="span">
                This flight only departs from {selectedAirport.name} ({selectedAirport.code}) - any connecting flights to or from {selectedAirport.code} must be purchased separately.
              </BodyText>
              <BodyText variant="small" weight="normal" as="span">
                Please ensure you leave enough time for transfers - you are responsible for arriving for this flight on time. Missed flights will not be refunded.
              </BodyText>
            </Group>
          }
          placement="bottom"
          portaled
        />
      )}
    </DropdownContainer>
  )
}

export default withOfferPageState(state => ({
  open: state.activeFilter === 'airport',
  selectedAirport: state.airport,
}), CharterFlightOfferAirportSelect)
